import React from 'react';
import { Mono } from '@jotpsych/ui';
import { Typography } from '@mui/material';

import { SECONDARY_PAYER_UNSET_LABEL } from '../../../utils/secondaryBillingPresentation';
import { formatCents, ProviderIdentity } from '../shared';
import { EraAgeChip, SecondaryBlockerChips } from '../work';

import type { HomeQueueRow } from './homeQueues';

/**
 * The cells only a wide column set carries (GDN-4068, `homeQueueColumns`),
 * each the Claims tab's own cell for the same column, so a claim reads the
 * same on both surfaces. Null for every other column, which the card renders
 * itself.
 *
 * Money uses the shared Mono primitive, as the Claims to submit cell does: it
 * carries the theme's mono stack and `tabular-nums`, which is what lines a
 * column of amounts up. A row with no secondary balance leaves the cell
 * blank rather than printing $0.00, as on the Claims tab.
 */
export const renderWideColumnCell = (columnKey: string, item: HomeQueueRow): React.ReactNode => {
  switch (columnKey) {
    case 'charge':
      return (
        <Mono sx={{ fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {formatCents(item.total_charge_cents ?? 0)}
        </Mono>
      );
    case 'provider_name':
      /* The rendering provider, and honest absence when there is none, as
         the Claims tab's Provider column says it (GDN-3243). */
      return (
        <ProviderIdentity
          missingLabel="No provider recorded"
          providerId={item.provider_id}
          providerName={item.clinician_display_name}
        />
      );
    case 'secondary_payer':
      return (
        <Typography
          color={item.secondary_payer_name ? 'text.primary' : 'text.secondary'}
          noWrap
          variant="dataMd"
        >
          {item.secondary_payer_name ?? SECONDARY_PAYER_UNSET_LABEL}
        </Typography>
      );
    case 'secondary_balance':
      return (
        <Mono sx={{ fontWeight: 500, whiteSpace: 'nowrap' }} variant="dataMd">
          {item.secondary_billable_cents == null ? '' : formatCents(item.secondary_billable_cents)}
        </Mono>
      );
    case 'era_age':
      return <EraAgeChip primaryEraAt={item.primary_era_at} />;
    case 'secondary_blockers':
      return <SecondaryBlockerChips blockers={item.secondary_blockers} />;
    default:
      return null;
  }
};
